import {
  useQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import { StatusesEnum } from '../services/generated/Api';
import type { UserProfileResponse } from '../services/generated/Api';
import type { PaginatedInventoryResponse, UserInventoryItemResponse } from '../services/api';
import { useAuthedInfiniteQuery, useAuthedQuery } from './useAuthedQuery';
import { playEarnings } from '../lib/uiSounds';

import { INVENTORY_FILTERS, type InventoryFilter } from './inventoryFilter';

export { DEFAULT_INVENTORY_FILTER, parseInventoryFilter, type InventoryFilter } from './inventoryFilter';

const PAGE_SIZE = 20;

function findCachedInventoryItem(
  queryClient: QueryClient,
  itemId: string,
): UserInventoryItemResponse | undefined {
  for (const filter of INVENTORY_FILTERS) {
    const cached = queryClient.getQueryData<InfiniteData<PaginatedInventoryResponse>>(queryKeys.inventory(filter));
    const found = cached?.pages.flatMap((page) => page.items).find((candidate) => candidate.id === itemId);
    if (found) return found;
  }
  return undefined;
}

function getStatusesForFilter(filter: InventoryFilter): StatusesEnum[] {
  switch (filter) {
    case 'available':
      return [StatusesEnum.AVAILABLE];
    case 'sold':
      return [StatusesEnum.SOLD, StatusesEnum.CONTRACT_CONSUMED, StatusesEnum.UPGRADE_CONSUMED];
    case 'withdrawn':
      return [StatusesEnum.PROCESSING, StatusesEnum.SENT, StatusesEnum.CONFIRMED];
  }
}

export function useInventoryInfinite(filter: InventoryFilter) {
  const apiClient = useApiClient();

  return useAuthedInfiniteQuery({
    queryKey: queryKeys.inventory(filter),
    queryFn: async ({ pageParam = 1 }) => {
      const statuses = getStatusesForFilter(filter);
      const response = await apiClient.api.inventoryControllerGetInventory({
        page: pageParam.toString(),
        limit: PAGE_SIZE.toString(),
        statuses,
      });
      return response;
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.hasMore ? lastPageParam + 1 : undefined,
    initialPageParam: 1,
    staleTime: 0,
  });
}

export function useInventoryItemDetails(
  itemId: string,
  enabled = true,
): UseQueryResult<UserInventoryItemResponse | undefined, Error> {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.inventoryItem(itemId),
    queryFn: () => apiClient.api.inventoryControllerGetInventoryItemDetails(itemId),
    enabled,
    refetchOnMount: 'always',
    placeholderData: () => findCachedInventoryItem(queryClient, itemId),
  });
}

export function useAvailableItemsInfo(enabled = false) {
  const apiClient = useApiClient();

  return useAuthedQuery({
    queryKey: ['inventory', 'availableInfo'],
    queryFn: async () => {
      const response = await apiClient.api.inventoryControllerGetInventory({
        page: '1',
        limit: '1000',
        statuses: [StatusesEnum.AVAILABLE],
      });

      const items = response.items;
      const totalCredits = items.reduce((sum, item) => sum + (item.acquiredPriceInCredits || 0), 0);
      return {
        count: items.length,
        totalCredits,
      };
    },
    enabled,
  });
}

export function useSellItem() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => apiClient.api.inventoryControllerSellInventoryItem(itemId),
    onSuccess: (data: { amountInCredits: number }) => {
      playEarnings();
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
      queryClient.setQueryData<UserProfileResponse>(queryKeys.profile, (old) =>
        old ? { ...old, balanceInCredits: old.balanceInCredits + data.amountInCredits } : old
      );
    },
  });
}

export function useSellAllItems() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.api.inventoryControllerSellAllInventoryItems(),
    onSuccess: () => {
      playEarnings();
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useSellItems() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemIds: string[]) => apiClient.api.inventoryControllerSellInventoryItems({ itemIds }),
    onSuccess: () => {
      playEarnings();
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}

export function useWithdrawItem() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => apiClient.api.inventoryControllerWithdrawItem(itemId, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useUpdateTradeLink() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tradeUrl: string) =>
      apiClient.api.inventoryControllerUpdateTradeLink({ tradeUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.profile });
    },
  });
}
